/**
 * VenueFlow Dropbox Service
 * 
 * Handles chunked uploads to Dropbox via Make.com token refresh.
 * Ported from Softr IPTV uploader.
 */

const MAKE_TOKEN_WEBHOOK = "https://hook.eu2.make.com/rwhw9id7f19mgbhg32x36sejhg0lrot6";
const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB chunks

// ============================================================
// DROPBOX TOKEN MANAGER
// ============================================================
class DropboxTokenManager {
  constructor(makeWebhookUrl) {
    this.makeWebhookUrl = makeWebhookUrl;
    this.accessToken = null;
    this.expiresAt = 0;
  }

  async getToken() {
    if (!this.accessToken || Date.now() >= this.expiresAt) {
      await this.fetchAccessTokenFromMake();
    }
    return this.accessToken;
  }

  async fetchAccessTokenFromMake() {
    const res = await fetch(this.makeWebhookUrl, { method: "POST" });
    if (!res.ok) {
      throw new Error("MAKE.com failed to provide Dropbox access_token");
    }
    const json = await res.json();
    this.accessToken = json.access_token;
    this.expiresAt = Date.now() + (json.expires_in - 30) * 1000;
  }
}

// ============================================================
// DROPBOX CHUNKED UPLOADER
// ============================================================
class DropboxUploader {
  constructor(tokenManager, chunkSize) {
    this.tokenManager = tokenManager;
    this.chunkSize = chunkSize;
  }

  async upload(file, path, onProgress, abortCtrl) {
    const sessionId = await this.startSession();
    await this.appendChunks(file, sessionId, onProgress, abortCtrl);
    const finish = await this.finishSession(file, sessionId, path);
    return await this.getTempLink(finish.path_lower);
  }

  async startSession() {
    const token = await this.tokenManager.getToken();
    const res = await fetch(
      "https://content.dropboxapi.com/2/files/upload_session/start",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Dropbox-API-Arg": JSON.stringify({ close: false }),
          "Content-Type": "application/octet-stream",
        },
        body: new Blob([]),
      }
    );
    if (!res.ok) {
      console.error("Dropbox startSession error:", await res.text());
      throw new Error("Failed to start Dropbox upload_session");
    }
    const json = await res.json();
    return json.session_id;
  }

  async appendChunks(file, sessionId, onProgress, abortCtrl) {
    let offset = 0;
    const total = file.size;

    while (offset < total) {
      const chunk = file.slice(offset, offset + this.chunkSize);
      const token = await this.tokenManager.getToken();

      const res = await fetch(
        "https://content.dropboxapi.com/2/files/upload_session/append_v2",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Dropbox-API-Arg": JSON.stringify({
              cursor: { session_id: sessionId, offset },
              close: false,
            }),
            "Content-Type": "application/octet-stream",
          },
          body: chunk,
          signal: abortCtrl ? abortCtrl.signal : undefined,
        }
      );

      if (!res.ok) {
        console.error("Dropbox append error:", await res.text());
        throw new Error("Failed to append Dropbox chunk");
      }

      offset += chunk.size;
      if (onProgress) onProgress(Math.round((offset / total) * 100));
    }
  }

  async finishSession(file, sessionId, path) {
    const token = await this.tokenManager.getToken();
    const res = await fetch(
      "https://content.dropboxapi.com/2/files/upload_session/finish",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Dropbox-API-Arg": JSON.stringify({
            cursor: { session_id: sessionId, offset: file.size },
            commit: {
              path,
              mode: "add",
              autorename: true,
              mute: false,
            },
          }),
          "Content-Type": "application/octet-stream",
        },
        body: new Blob([]),
      }
    );

    if (!res.ok) {
      console.error("Dropbox finish error:", await res.text());
      throw new Error("Failed to finish Dropbox upload");
    }

    return res.json();
  }

  async getTempLink(pathLower) {
    const token = await this.tokenManager.getToken();
    const res = await fetch(
      "https://api.dropboxapi.com/2/files/get_temporary_link",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path: pathLower }),
      }
    );

    if (!res.ok) {
      console.error("Dropbox get_temporary_link error:", await res.text());
      throw new Error("Failed to get temporary Dropbox link");
    }

    const json = await res.json();
    return json.link;
  }
}

// ============================================================
// SINGLETON INSTANCES
// ============================================================
const tokenManager = new DropboxTokenManager(MAKE_TOKEN_WEBHOOK);
export const dropboxUploader = new DropboxUploader(tokenManager, CHUNK_SIZE);

// ============================================================
// CONVENIENCE UPLOAD FUNCTION
// ============================================================
export async function uploadToDropbox(file, path, onProgress, abortController) {
  console.log("🔥 Dropbox upload start:", { path, file: file.name });
  const url = await dropboxUploader.upload(file, path, onProgress, abortController);
  console.log("✅ Dropbox upload finished:", url);
  return url;
}

export default dropboxUploader;
